var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/ban-types */
import { useAppDispatch, useAppSelector } from '../../../components/app/hooks';
import React, { useEffect, useMemo, useRef } from 'react';
import { addInstance, removeInstance, updateLoadingState } from '../../../redux/loadingSlice';
import 'react-loading-skeleton/dist/skeleton.css';
import './loader.scss';
import { DefaultLoadingComponent } from './default-loading-component';
import { getPaddedProps } from './loader-helper';
import { LoaderContext } from './loader-wrapper';
/**
 * Custom hook to manage loading state and display
 * @template ID - The identifier type for loaders
 * @param {Props<ID>} props - Properties for the useLoader hook
 * @returns {ReturnType<ID>} Object containing the component to display, loading state, and loading state setter
 */
export function useLoader(_a) {
    var _b;
    var _c, _d;
    var identifier = _a.identifier, _e = _a.componentToBeHidden, _componentToBeHidden = _e === void 0 ? function () { return React.createElement(React.Fragment, null); } : _e, _f = _a.loaderLineHeight, loaderLineHeight = _f === void 0 ? NaN : _f, _g = _a.loaderNumberOfLines, loaderNumberOfLines = _g === void 0 ? 5 : _g, _h = _a.CustomLoader, CustomLoader = _h === void 0 ? null : _h, _j = _a.loaderProps, loaderProps = _j === void 0 ? {} : _j, _k = _a.type, type = _k === void 0 ? 'default' : _k;
    var isMounted = useRef(false);
    var loadingIdentifiers = useAppSelector(function (state) { return state.loadingStore.loadingIdentifiers; });
    // const isLoading = useAppSelector((state) => state.loadingStore.isLoading);
    var dispatch = useAppDispatch();
    var isLoaderEnabled = useAppSelector(function (state) { return state.loadingStore.isLoaderEnabled; });
    var _paddedProps = useMemo(function () {
        return getPaddedProps(loaderProps);
    }, __spreadArray([], getPaddedProps(loaderProps), true));
    var setLoadingState = function (state) {
        dispatch(updateLoadingState({ identifier: identifier, isLoading: state }));
    };
    useEffect(function () {
        var _a, _b;
        if (!isMounted.current) {
            isMounted.current = true;
            dispatch(addInstance({ identifier: identifier }));
            if (((_a = loadingIdentifiers[identifier]) === null || _a === void 0 ? void 0 : _a.instanceCount) === 0 ||
                ((_b = loadingIdentifiers[identifier]) === null || _b === void 0 ? void 0 : _b.instanceCount) === undefined) {
                setLoadingState(true);
            }
        }
        return function () {
            var _a;
            isMounted.current = false;
            dispatch(removeInstance({ identifier: identifier }));
            if (((_a = loadingIdentifiers[identifier]) === null || _a === void 0 ? void 0 : _a.instanceCount) === 0) {
                setLoadingState(false);
            }
        };
    }, []);
    var currentInstances = useMemo(function () {
        var _a, _b;
        return (_b = (_a = loadingIdentifiers[identifier]) === null || _a === void 0 ? void 0 : _a.instanceCount) !== null && _b !== void 0 ? _b : 0;
    }, [(_c = loadingIdentifiers[identifier]) === null || _c === void 0 ? void 0 : _c.instanceCount]);
    var isLoading = useMemo(function () {
        var _a;
        return (!isMounted.current || currentInstances === 0 || !currentInstances || ((_a = loadingIdentifiers[identifier]) === null || _a === void 0 ? void 0 : _a.isLoading));
    }, [(_d = loadingIdentifiers[identifier]) === null || _d === void 0 ? void 0 : _d.isLoading, isMounted.current, currentInstances]);
    var _Component = useMemo(function () {
        if (type === 'ruLoader')
            return function (_a) {
                var props = __rest(_a, []);
                return _componentToBeHidden(__assign({}, props));
            };
        if (isLoading && isLoaderEnabled)
            return CustomLoader
                ? function () { return (React.createElement("div", null,
                    CustomLoader,
                    React.createElement("div", { style: { display: 'none' } }))); }
                : function () { return (React.createElement(React.Fragment, null,
                    React.createElement(DefaultLoadingComponent, __assign({}, __assign({ count: loaderNumberOfLines, height: loaderLineHeight }, loaderProps))))); };
        // eslint-disable-next-line react/display-name
        return function (_a) {
            var props = __rest(_a, []);
            return _componentToBeHidden(__assign({}, props));
        };
    }, __spreadArray([isLoading, isLoaderEnabled], _paddedProps, true));
    var getUpperCaseString = function (_identifier) {
        return "".concat(_identifier.charAt(0).toUpperCase() + _identifier.slice(1));
    };
    return _b = {},
        _b["".concat(getUpperCaseString(identifier), "ComponentToDisplay")] = function (_a) {
            var props = __rest(_a, []);
            return (React.createElement(LoaderContext.Provider, { value: { isLoading: !(!isLoading || !isLoaderEnabled) } },
                React.createElement(_Component, __assign({}, props))));
        },
        _b["".concat(identifier, "IsLoading")] = !(!isLoading || !isLoaderEnabled),
        _b["".concat(identifier, "SetIsLoading")] = setLoadingState,
        _b;
}
